import * as React from "react";
import {
    Banner,
    Button,
    Card,
    CardBody,
    EmptyState,
    ListStates,
    PageContainer,
    PageHeader,
    SearchInput,
    Select,
    Textarea,
    Toolbar,
} from "../components";
import { Tabs } from "../components/Tabs";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { useStudentNames } from "../students/StudentDirectory";
import * as Management from "../management/ManagementStore";
import * as Store from "./SubjectChoiceStore";
import * as Rules from "./subjectChoiceEvaluator";
import { SubjectChoiceGridTab } from "./SubjectChoiceGridTab";
import { SubjectChoiceStudentTab } from "./SubjectChoiceStudentTab";
import { SubjectChoiceSummaryTab } from "./SubjectChoiceSummaryTab";

/**
 * Subject choices for a whole grade, checked against the grade's subject-choice rules as you tick.
 *
 * A qualification module is one subject in one *term*, so the per-row Module enrollment screen needs four rows
 * to say "takes Mathematics". This screen works in subjects: ticking a subject enrols the student in every term of
 * it in the grade, and also places them in the grade if they were not already. Unticking sets those enrollments to
 * Inactive (never deletes them), and a Completed term cannot be changed from here.
 *
 * This component owns the grade, the choices, the changed rows and the save; the three tabs below it are views over
 * the same state. Rules are evaluated live by `subjectChoiceEvaluator.ts`, a copy of the server's evaluator, so a row
 * shows its problems before anyone presses Save - but the server checks again and has the final word. Only rows
 * that changed are sent, so two people editing different students do not overwrite each other.
 */

export type Choices = Record<string, string[]>;

/** Everything a tab needs, computed once here so the three views can never disagree. */
export interface SubjectChoiceModel {
    subjects: Store.SubjectChoiceSubject[];
    offering: Rules.Offering;
    /** Rules this grade can apply this year, in rank order. */
    activeRules: Rules.RuleDefinition[];
    choices: Choices;
    /** Every student in the grid, in the order they were loaded or added. */
    order: string[];
    /** The students the search lets through, sorted by name. */
    visible: string[];
    changed: string[];
    canWrite: boolean;
    names: (studentID: string) => string;
    subjectName: (moduleID: string) => string;
    isLocked: (studentID: string, moduleID: string) => boolean;
    /** Why ticking this subject is not allowed right now, or undefined when it is. */
    blockedReason: (studentID: string, moduleID: string) => string | undefined;
    findings: (studentID: string) => Rules.Finding[];
    toggle: (studentID: string, moduleID: string, on: boolean) => void;
}

const sameSet = (a: string[] = [], b: string[] = []) => a.length === b.length && a.every((x) => b.includes(x));

type TabKey = "grid" | "student" | "summary";

export const SubjectChoicesScreen: React.FC = () => {
    const toast = useToast();
    const canWrite = usePermission(Permissions.ENROLLMENT);

    const levels = useAsync(() => Management.listQualificationProgramLevels(), [], { initial: [] });
    const [programLevelID, setProgramLevelID] = React.useState("");
    const grid = useAsync(() => Store.getSubjectChoices(programLevelID), [programLevelID], { enabled: !!programLevelID });

    const [original, setOriginal] = React.useState<Choices>({});
    const [choices, setChoices] = React.useState<Choices>({});
    const [order, setOrder] = React.useState<string[]>([]);
    const [search, setSearch] = React.useState("");
    const [pasted, setPasted] = React.useState("");
    const [tab, setTab] = React.useState<TabKey>("grid");
    // What the server said when it last refused a save; cleared as soon as that student is edited again.
    const [rejected, setRejected] = React.useState<Record<string, Store.StudentFinding[]>>({});

    // A fresh load (new grade, or after a save) resets the edit state to what the server holds.
    React.useEffect(() => {
        const students = grid.data?.students ?? [];
        const loaded: Choices = {};
        students.forEach((s) => (loaded[s.studentID] = s.moduleIDs));
        setOriginal(loaded);
        setChoices(loaded);
        setOrder(students.map((s) => s.studentID));
        setRejected({});
    }, [grid.data]);

    const names = useStudentNames(order);
    const subjects = grid.data?.subjects ?? [];

    const offering = React.useMemo<Rules.Offering>(
        () => ({
            rules: (grid.data?.rules ?? []).map((r) => ({
                id: r.id, name: r.name, appliesToAllSubjects: r.appliesToAllSubjects, measure: r.measure,
                minimum: r.minimum, maximum: r.maximum, moduleIDs: r.moduleIDs,
            })),
            subjects: subjects.map((s) => ({ moduleID: s.moduleID, name: s.name, credits: s.credits })),
            corequisites: grid.data?.corequisites ?? [],
        }),
        [grid.data, subjects]
    );
    const activeRules = React.useMemo(() => Rules.activeRules(offering), [offering]);
    const exactlyOneRules = React.useMemo(() => activeRules.filter(Rules.isExactlyOne), [activeRules]);
    const compulsory = React.useMemo(
        () => Array.from(new Set(activeRules.filter((r) => Rules.isCompulsory(r, offering)).flatMap((r) => Rules.membersOf(r, offering.subjects)))),
        [activeRules, offering]
    );

    const completed = React.useMemo(() => {
        const map: Record<string, string[]> = {};
        (grid.data?.students ?? []).forEach((s) => (map[s.studentID] = s.completedModuleIDs));
        return map;
    }, [grid.data]);
    const isLocked = React.useCallback((studentID: string, moduleID: string) => (completed[studentID] ?? []).includes(moduleID), [completed]);
    const subjectName = React.useCallback((id: string) => subjects.find((s) => s.moduleID === id)?.name ?? id, [subjects]);

    // Not memoised: `names` is a stable function whose results change as the directory resolves, so a memo
    // keyed on it would keep sorting by raw ids.
    const visible = (() => {
        const term = search.trim().toLowerCase();
        const rows = order.filter((id) => !term || id.toLowerCase().includes(term) || names(id).toLowerCase().includes(term));
        return rows.sort((a, b) => names(a).localeCompare(names(b)));
    })();

    const changed = order.filter((id) => !(id in original) || !sameSet(original[id], choices[id]));

    // A Completed subject is kept by every save, so it always counts toward the rules.
    const findings = React.useCallback(
        (studentID: string) => {
            const chosen = Array.from(new Set([...(choices[studentID] ?? []), ...(completed[studentID] ?? [])]));
            return Rules.studentFindings(offering, chosen);
        },
        [choices, completed, offering]
    );

    /** Ticking one subject of an exactly-one rule unticks the others - unless one of them is locked as Completed. */
    const blockedReason = React.useCallback(
        (studentID: string, moduleID: string) => {
            const mine = choices[studentID] ?? [];
            if (mine.includes(moduleID)) return undefined;
            for (const rule of exactlyOneRules) {
                if (!rule.moduleIDs.includes(moduleID)) continue;
                const locked = rule.moduleIDs.find((other) => other !== moduleID && mine.includes(other) && isLocked(studentID, other));
                if (locked)
                    return `${subjectName(locked)} has a Completed term and cannot be dropped here, so ${subjectName(moduleID)} cannot replace it (${rule.name}).`;
            }
            return undefined;
        },
        [choices, exactlyOneRules, isLocked, subjectName]
    );

    const toggle = React.useCallback(
        (studentID: string, moduleID: string, on: boolean) => {
            if (on && blockedReason(studentID, moduleID)) return;
            setChoices((current) => {
                const set = new Set(current[studentID] ?? []);
                if (on) {
                    exactlyOneRules
                        .filter((r) => r.moduleIDs.includes(moduleID))
                        .forEach((r) => r.moduleIDs.filter((other) => other !== moduleID && !isLocked(studentID, other)).forEach((other) => set.delete(other)));
                    set.add(moduleID);
                } else set.delete(moduleID);
                return { ...current, [studentID]: Array.from(set) };
            });
            setRejected((current) => {
                if (!(studentID in current)) return current;
                const next = { ...current };
                delete next[studentID];
                return next;
            });
        },
        [blockedReason, exactlyOneRules, isLocked]
    );

    const fillCompulsory = () => {
        let filled = 0;
        visible.forEach((studentID) =>
            compulsory.forEach((moduleID) => {
                if ((choices[studentID] ?? []).includes(moduleID) || blockedReason(studentID, moduleID)) return;
                toggle(studentID, moduleID, true);
                filled++;
            })
        );
        toast.info(filled ? `Ticked ${filled} compulsory subject choice${filled === 1 ? "" : "s"}.` : "Every student shown already has the compulsory subjects.");
    };

    const addStudents = () => {
        const ids = pasted
            .split(/[\s,;]+/)
            .map((id) => id.trim())
            .filter((id) => id && !order.includes(id));
        if (!ids.length) return;
        setOrder((current) => [...current, ...ids]);
        // New students start with the compulsory subjects ticked; the rest is up to whoever is choosing.
        setChoices((current) => {
            const next = { ...current };
            ids.forEach((id) => (next[id] = [...compulsory]));
            return next;
        });
        setPasted("");
        toast.info(`Added ${ids.length} student${ids.length === 1 ? "" : "s"} with the compulsory subjects ticked. Choose the rest, then save.`);
    };

    const needFixing = changed.filter((id) => findings(id).length > 0);

    const save = useAction(async () => {
        try {
            const result = await Store.saveSubjectChoices(
                programLevelID,
                changed.map((studentID) => ({ studentID, moduleIDs: choices[studentID] ?? [] }))
            );
            const parts = [
                result.moduleEnrollmentsCreated && `${result.moduleEnrollmentsCreated} enrolments created`,
                result.moduleEnrollmentsReactivated && `${result.moduleEnrollmentsReactivated} reactivated`,
                result.moduleEnrollmentsDeactivated && `${result.moduleEnrollmentsDeactivated} set inactive`,
                result.levelEnrollmentsCreated && `${result.levelEnrollmentsCreated} students placed in the grade`,
            ].filter(Boolean);
            toast.success(parts.length ? `Saved: ${parts.join(", ")}.` : "Saved. Nothing needed to change.");
            grid.refresh();
        } catch (error) {
            if (error instanceof Store.SubjectChoiceRulesError) {
                const byStudent: Record<string, Store.StudentFinding[]> = {};
                error.findings.forEach((f) => (byStudent[f.studentID] = [...(byStudent[f.studentID] ?? []), f]));
                setRejected(byStudent);
            }
            throw error;
        }
    });

    const levelOptions = React.useMemo(
        () =>
            (levels.data ?? [])
                .map((l) => ({ id: l.id ?? "", label: Management.programLevelLabel(l) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [levels.data]
    );

    const model: SubjectChoiceModel = {
        subjects, offering, activeRules, choices, order, visible, changed, canWrite, names, subjectName, isLocked, blockedReason,
        // The server's word wins for a row it rejected until that row is edited again.
        findings: (studentID) =>
            rejected[studentID]?.map((f) => ({ kind: f.kind, ruleID: f.ruleID, message: f.message })) ?? findings(studentID),
        toggle,
    };

    const gradeFindings = grid.data?.gradeFindings ?? [];
    const rejectedStudents = Object.keys(rejected);

    return (
        <PageContainer>
            <PageHeader
                title="Subject choices"
                icon={["fad", "list-check"]}
                description="Choose subjects for a whole grade at once. Ticking a subject enrols the student in every term of it, and every choice is checked against the grade's subject choice rules."
                actions={
                    canWrite &&
                    programLevelID && (
                        <Button
                            icon={["fad", "floppy-disk"]}
                            onClick={save.run}
                            loading={save.busy}
                            disabled={changed.length === 0 || needFixing.length > 0}
                            title={needFixing.length ? "Fix the highlighted students first" : undefined}
                        >
                            {changed.length ? `Save ${changed.length} student${changed.length === 1 ? "" : "s"}` : "Save"}
                        </Button>
                    )
                }
            />

            <Toolbar>
                <div className="w-full max-w-md">
                    <label className="mb-1 block text-sm font-medium text-text">Grade</label>
                    <Select
                        value={programLevelID}
                        onChange={(e) => setProgramLevelID(e.target.value)}
                        placeholder={levels.isLoading ? "Loading..." : "Select a grade (program level)"}
                    >
                        {levelOptions.map((o) => (
                            <option key={o.id} value={o.id}>
                                {o.label}
                            </option>
                        ))}
                    </Select>
                </div>
                {programLevelID && <SearchInput value={search} onChange={setSearch} placeholder="Find a student" />}
                {programLevelID && canWrite && compulsory.length > 0 && (
                    <Button variant="outline-secondary" icon={["fad", "check-double"]} onClick={fillCompulsory}>
                        Fill compulsory subjects
                    </Button>
                )}
            </Toolbar>

            {needFixing.length > 0 && (
                <Banner tone="warning" className="mb-4">
                    {`${needFixing.length} student${needFixing.length === 1 ? " needs" : "s need"} fixing before you can save. Their rows are highlighted.`}
                </Banner>
            )}

            {save.error && (
                <Banner tone="danger" className="mb-4">
                    <div>{save.error}</div>
                    {rejectedStudents.length > 0 && (
                        <ul className="mt-2 list-disc pl-5">
                            {rejectedStudents.map((id) => (
                                <li key={id}>
                                    <span className="font-medium">{names(id)}</span>: {rejected[id].map((f) => f.message).join(" ")}
                                </li>
                            ))}
                        </ul>
                    )}
                </Banner>
            )}

            {gradeFindings.length > 0 && (
                <Banner tone="info" className="mb-4">
                    <div className="font-medium">Some rules are not checked for this grade this year:</div>
                    <ul className="mt-1 list-disc pl-5">
                        {gradeFindings.map((f, i) => (
                            <li key={i}>{f.message}</li>
                        ))}
                    </ul>
                </Banner>
            )}

            {!programLevelID && (
                <Card>
                    <EmptyState
                        icon={["fad", "list-check"]}
                        title="Pick a grade"
                        hint="Students placed in the grade are listed with a column per subject the grade offers."
                    />
                </Card>
            )}

            {programLevelID && (
                <ListStates
                    isLoading={grid.isLoading}
                    error={grid.error}
                    onRetry={grid.refresh}
                    isEmpty={subjects.length === 0}
                    empty={{
                        icon: ["fad", "cubes"],
                        title: "This grade offers no subjects yet",
                        hint: "Subjects come from the grade's qualification modules. Create them under Management, or apply a curriculum template.",
                    }}
                >
                    <Tabs
                        tabs={[
                            { key: "grid", label: "Grid", icon: ["fad", "table-cells"] },
                            { key: "student", label: "By student", icon: ["fad", "user"] },
                            { key: "summary", label: "Summary", icon: ["fad", "chart-simple"] },
                        ]}
                        active={tab}
                        onChange={(key) => setTab(key as TabKey)}
                    />

                    <Card>
                        {order.length === 0 ? (
                            <EmptyState
                                icon={["fad", "user-plus"]}
                                title="No students in this grade yet"
                                hint="Add them by identity user id below. Saving places them in the grade as well."
                            />
                        ) : tab === "grid" ? (
                            <SubjectChoiceGridTab model={model} />
                        ) : tab === "student" ? (
                            <SubjectChoiceStudentTab model={model} />
                        ) : (
                            <SubjectChoiceSummaryTab model={model} />
                        )}

                        {canWrite && tab !== "summary" && (
                            <CardBody className="border-t border-border p-6">
                                <label className="mb-1 block text-sm font-medium text-text">Add students</label>
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                                    <Textarea
                                        rows={2}
                                        className="flex-1"
                                        value={pasted}
                                        onChange={(e) => setPasted(e.target.value)}
                                        placeholder="Identity user ids, one per line or comma-separated"
                                    />
                                    <Button variant="outline-secondary" icon={["fad", "user-plus"]} onClick={addStudents} disabled={!pasted.trim()}>
                                        Add to grid
                                    </Button>
                                </div>
                                <p className="mt-1 text-xs text-text-weak">
                                    Students are identity-server users, not rows in this database. Saving places added students in the
                                    grade, along with whichever subjects you tick.
                                </p>
                            </CardBody>
                        )}
                    </Card>
                </ListStates>
            )}
        </PageContainer>
    );
};

export default SubjectChoicesScreen;
